import React, { useState } from 'react';

/**
 * This component uses the project's UI packages if available.
 * It renders a compact version badge with an optional copy button and link.
 *
 * Props:
 * - version: explicit version string (falls back to env values)
 * - label: optional label shown before the version (e.g. "v")
 * - href: optional URL to link the badge to (release notes / changelog)
 * - showCopy: show a copy-to-clipboard button (default true)
 */
interface VersionComponentProps {
    version?: string;
    label?: string;
    href?: string;
    showCopy?: boolean;
    className?: string;
}

export const getDefaultVersion = (): string => {
    // Common env fallbacks for different build systems
    // Replace/add more as your project uses (VITE_, REACT_APP_, npm_package_version, etc.)
    // These are safe no-op reads in typical front-end builds.
    // If none are set, show "dev".
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (process as any).env ?? {};
    return (
        env.REACT_APP_VERSION ||
        env.VITE_VERSION ||
        env.VITE_APP_VERSION ||
        env.NPM_PACKAGE_VERSION ||
        env.npm_package_version ||
        'dev'
    );
};

export default function VersionComponent({
    version,
    label = 'v',
    href,
    showCopy = false,
    className
}: VersionComponentProps) {
    const ver = version ?? getDefaultVersion();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(ver);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1200);
        } catch {
            // ignore copy errors
        }
    };

    // Simple presentational fallback badge + button; intended to blend with the project's UI packages.
    // If your project exposes Badge / IconButton components, you can swap these elements for them.
    const badge = (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 8px',
                borderRadius: 999,
                background: 'rgba(0,0,0,0.06)',
                color: 'var(--color-text, #111)',
                fontSize: 12,
                fontWeight: 600
            }}
        >
            <span style={{ opacity: 0.85, color: '#fff' }}>{label}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', color: '#fff' }}>{ver}</span>
        </span>
    );

    return (
        <div className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {href ? (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none' }}
                >
                    {badge}
                </a>
            ) : (
                badge
            )}

            {showCopy && (
                <button
                    onClick={handleCopy}
                    aria-label="Copy version"
                    title={copied ? 'Copied' : 'Copy version'}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 6
                    }}
                >
                    {/* simple clipboard icon */}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path
                            d="M16 4h-4l-2-2H8a2 2 0 0 0-2 2v1"
                            stroke="white"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <rect
                            x="8"
                            y="6"
                            width="12"
                            height="14"
                            rx="2"
                            stroke="white"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        {copied ? (
                            <path
                                d="M9 13l2 2 4-4"
                                stroke="white"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        ) : null}
                    </svg>
                </button>
            )}
        </div>
    );
}
